import { useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import footprintHtml from '@/assets/footprint-year.html?raw';
import { GameFrame, Spinner } from './EnvLayout';

/** Built-in categories keep their themed (light/dark aware) colours. */
const THEMED: Record<string, string> = {
  drinks: '--c-drinks', pack: '--c-pack', paper: '--c-paper', life: '--c-life',
};

const asScriptJson = (v: unknown) =>
  JSON.stringify(v)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');

/**
 * Footprint Year calculator. The standalone page runs in an iframe; we inject
 * the admin-managed categories and objects as window.__FOOTPRINT_CATS__ /
 * __FOOTPRINT_ITEMS__ before its script runs (if they can't be loaded it falls
 * back to its built-in lists).
 */
export default function FootprintGame() {
  const { data, isLoading } = trpc.environmental.carbon.useQuery();

  const doc = useMemo(() => {
    if (!data) return footprintHtml;
    const cats = data.categories.map(c => ({
      id: c.slug, name: c.name,
      ...(c.color ? { color: c.color } : { c: THEMED[c.slug] ?? '--c-drinks' }),
    }));
    const known = new Set(cats.map(c => c.id));
    const items = data.items
      .filter(i => known.has(i.category))
      .map(i => ({
        id: String(i.id), cat: i.category, name: i.name, unit: i.unit,
        co2: i.co2, mj: i.mj, felled: i.felled, tip: i.tip,
      }));
    // Function replacement: a string would treat "$&" etc. in the data specially.
    return footprintHtml.replace('<script>', () =>
      `<script>window.__FOOTPRINT_CATS__=${asScriptJson(cats)};window.__FOOTPRINT_ITEMS__=${asScriptJson(items)};</script>\n<script>`);
  }, [data]);

  if (isLoading) return <Spinner />;
  return <GameFrame html={doc} title="Carbon calculator" />;
}
